import React from "react";
import type { ApolloClient } from "@apollo/client";
import moment from "moment";
import { isEmpty, some, transform } from "lodash";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { userService } from "services/userService";
import { adminService } from "services/adminService";
import { updateCompanyInfo, updateCompanyKybInfo } from "actions/adminActions";
import { Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import Button from "components/Button";
import AddressValidator from "components/AddressValidator";
import {
  EmployerAddress1Validator,
  EmployerAddress2Validator,
  postalCodeValidator,
  testEIN,
  testPhoneNumber,
  urlValidator,
  validateUsersAddress,
  PRINTABLE_ASCII_MESSAGE,
  testPrintableAsciiCharacters,
  ALPHANUMERIC_SPACES_MESSAGE,
  testAlphanumericSpaces,
  noWhitespaceRegex,
  whiteSpaceError,
  INVALID_PHONE_NUMBER,
  stateValidator,
} from "utils/fieldValidators";
import {
  businessTypes,
  companySizeValues,
  naicsValues,
} from "statics/businessCodes";
import MaskedInput from "components/MaskedInput";
import StateSelect from "components/StateSelect";
import { toast } from "react-toastify";
import { wrapError } from "utils/errorHelper";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import {
  companionPlanId,
  primaryPlanId,
  statePlanId,
} from "statics/featureIds";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import { EmployerTermsComplete } from "statics/states";
import { ScrollToFieldError } from "utils/form";
import * as yup from "yup";
import type { RootState } from "types/state";

const schema = yup.object({
  businessName: yup
    .string()
    .label("Company Legal Name")
    .required()
    .min(3, "Must be at least three characters.")
    .max(200, "Must be less than 200 characters."),
  businessDBA: yup
    .string()
    .label("Company DBA")
    .min(1, "Must be at least one character.")
    .max(200),
  ein: yup
    .string()
    .label("EIN")
    .required()
    .test("ein", "${path} is not a valid format. Ex: 12-3456789", testEIN)
    .max(10),
  businessType: yup.string().label("Incorporation Type").required(),
  naicsCode: yup.string().label("Industry Type").required(),
  businessSize: yup.string().label("Company Size").required(),
  businessURL: yup
    .string()
    .label("Website URL")
    .max(200)
    .test("businessURL", "Enter a valid website URL", urlValidator),

  address1: EmployerAddress1Validator,
  address2: EmployerAddress2Validator,
  city: yup
    .string()
    .label("City")
    .required()
    .test("city", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test("city", ALPHANUMERIC_SPACES_MESSAGE, testAlphanumericSpaces)
    .max(40)
    .matches(noWhitespaceRegex, whiteSpaceError),
  state: stateValidator,
  postalCode: postalCodeValidator,
  phone: yup
    .string()
    .trim()
    .label("Phone")
    .required()
    .test("phone", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test("us-phone", INVALID_PHONE_NUMBER, testPhoneNumber)
    .max(30),
  phoneExt: yup.string().label("Phone Ext").max(10),
  registrationDate: yup
    .string()
    .test(
      "is-date",
      "Please enter a valid date in MM/DD/YYYY format",
      (value) => {
        if (!value) return false;
        const date = moment(value, "MM/DD/YYYY", true);
        return date.isValid();
      }
    )
    .label("Date of Incorporation")
    .required()
    // yup's string `.max()` is typed for length (number); the original JS passed a
    // Date here. Preserved as-is — boundary-cast keeps the exact runtime argument.
    .max(
      new Date() as unknown as number,
      "Date of Incorporation cannot be in the future"
    ),
});

function getEnabledFeaturesObj(
  enabledProductFeatures: Array<number | null> | null | undefined
): Record<string, boolean> {
  return transform(
    // lodash `transform` handles null/undefined at runtime (returns the
    // accumulator); its overloads reject those, so cast to the array element type.
    enabledProductFeatures as Array<number | null>,
    (acc: Record<string, boolean>, featureId) => {
      acc[featureId as number] = true;
      return acc;
    },
    {}
  );
}

/**
 * Formik values for the company form. Every field is a string because
 * `initialValues` (State.company) initializes them all to "".
 */
interface CompanyFormValues {
  businessName: string;
  businessDBA: string;
  ein: string;
  businessType: string;
  naicsCode: string;
  businessSize: string;
  businessURL: string;
  addressId: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  phoneExt: string;
  registrationDate: string;
}

type FormValues = CompanyFormValues;

interface PlanType {
  id: number;
  label: string;
}

const actions = [
  adminConstants.REGISTER_KYB_COMPANY,
  adminConstants.UPDATE_COMPANY_INFO,
];
const errorSelector = createErrorSelector(actions);
const loadingSelector = createLoadingSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: loadingSelector(state),
    error: errorSelector(state),
  };
};
const mapDispatchToProps = {
  updateCompanyInfo,
  updateCompanyKybInfo,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
  productFeatures?: object;
  onUpdate?: () => void;
  onBack?: () => void;
  hasPassedKyb?: boolean;
}

type Props = WithApolloClient<PropsFromRedux & OwnProps>;

interface State {
  company: CompanyFormValues;
  submitted: boolean;
  loading: boolean;
  showAddressModal: boolean;
  fetchingAddress: boolean;
  validatedAddress: object;
  planTypeError: string;
  planTypes: PlanType[];
  enabledProductFeatures: Record<string, boolean>;
  companyState?: string | null;
}

class Company extends React.Component<Props, State> {
  static defaultProps = {
    hasPassedKyb: false,
  };

  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      company: {
        businessName: "",
        businessDBA: "",
        ein: "",
        businessType: "",
        naicsCode: "",
        businessSize: "",
        businessURL: "",
        addressId: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        phone: "",
        phoneExt: "",
        registrationDate: "",
      },
      submitted: false,
      loading: true,
      showAddressModal: false,
      fetchingAddress: false,
      validatedAddress: {},
      planTypeError: "",
      planTypes: [
        { id: primaryPlanId, label: "Primary" },
        { id: companionPlanId, label: "Companion" },
        { id: statePlanId, label: "State" },
      ],
      enabledProductFeatures: {},
    };

    void adminService
      .getCompany(
        this.props.client as ApolloClient<object>,
        this.props.companyId!
      )
      .then((data) => {
        const company: CompanyFormValues = {
          businessName: data.name ?? "",
          businessDBA: data.dba ?? "",
          ein: data.ein ?? "",
          businessType: data.type ?? "",
          naicsCode: data.naicsCode ?? "",
          businessSize: data.size ?? "",
          businessURL: data.url ?? "",
          // found-not-fixed: `companyById` selection has no `addressId` field, so
          // this is always undefined -> "". The real addressId is read below from
          // data.address[0].id. Cast preserves the (no-op) original access.
          addressId: (data as { addressId?: string }).addressId ?? "",
          registrationDate: data.registrationDate
            ? moment(data.registrationDate).format("MM/DD/YYYY")
            : "",
          address1: "",
          address2: "",
          city: "",
          state: "",
          postalCode: "",
          phone: "",
          phoneExt: "",
        };

        if (Array.isArray(data.address) && data.address.length >= 1) {
          company.address1 = data.address[0].address1 ?? "";
          company.address2 = data.address[0].address2 ?? "";
          company.city = data.address[0].city ?? "";
          company.state = data.address[0].state ?? "";
          company.postalCode = data.address[0].postalCode ?? "";
          company.phone = data.address[0].phone ?? "";
          company.phoneExt = data.address[0].phoneExt ?? "";
          company.addressId = data.address[0].id ?? "";
        }

        const enabledProductFeatures = getEnabledFeaturesObj(
          data.enabledProductFeatures
        );

        this.setState({
          submitted: true,
          company,
          companyState: data.currentState,
          loading: false,
          enabledProductFeatures,
        });
      });
  }

  submitCompany = async (
    values: FormValues,
    skipAddressValidation = false
  ): Promise<void> => {
    const hasASelectedPlanType = some(
      this.state.enabledProductFeatures,
      (isChecked, key) => {
        if (
          // `key` is a string (lodash `some` object key); the plan ids are
          // numbers. Original JS used loose `==`; cast preserves that coercion.
          key == (primaryPlanId as unknown as string) ||
          key == (companionPlanId as unknown as string) ||
          key == (statePlanId as unknown as string)
        ) {
          return isChecked;
        }
        return false;
      }
    );

    if (!hasASelectedPlanType) {
      this.setState({
        planTypeError: "You must select at least one plan type.",
      });
      return;
    } else if (this.state.planTypeError) {
      this.setState({
        planTypeError: "",
      });
    }

    // verify user address, if it matches USPS just continue on
    // if USPS endpoint fails continue on
    // if there is a mismatch then open modal to allow customer to decide which to use

    if (!skipAddressValidation) {
      try {
        this.setState({ fetchingAddress: true });

        const companyAddress = {
          address1: values.address1,
          address2: values.address2,
          state: values.state,
          postalCode: values.postalCode,
          city: values.city,
        };

        const validatedAddress = await userService.getValidatedAddress(
          this.props.client as ApolloClient<object>,
          companyAddress
        );

        this.setState({ fetchingAddress: false });

        const userAddressIsValid = validateUsersAddress(
          companyAddress,
          // getValidatedAddress returns `{ ...string|null } | null`; the original
          // JS passed it through unguarded. Cast preserves the exact value and the
          // throw-on-null behavior at the property reads inside validateUsersAddress.
          validatedAddress as unknown as Parameters<
            typeof validateUsersAddress
          >[1]
        );

        if (!userAddressIsValid) {
          this.setState({
            showAddressModal: true,
            // state.validatedAddress is `object`; the GraphQL result is
            // `{ ...string|null } | null`. Original JS stored it as-is.
            validatedAddress: validatedAddress as unknown as object,
          });

          return;
        }
      } catch (error) {
        // if the validation endpoint fails we dont want to prevent user from saving so continue on and log error
        console.log(error);
      }
    }

    const addressId = this.state.company.addressId || "";

    const company = {
      // companyId is optional in props but the original passed it through
      // unguarded; the mutation variables require a present id.
      id: this.props.companyId!,
      name: values.businessName,
      dba: values.businessDBA,
      ein: values.ein,
      type: values.businessType,
      naicsCode: values.naicsCode,
      size: values.businessSize,
      url: values.businessURL,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      state: values.state,
      postalCode: values.postalCode,
      phone: values.phone,
      phoneExt: values.phoneExt,
      addressId,
      registrationDate: values.registrationDate,
    };

    const enabledProductFeatures = transform(
      this.state.enabledProductFeatures,
      (acc: number[], isEnabled, featureId) => {
        if (isEnabled) {
          acc.push(+featureId);
        }
        return acc;
      },
      [] as number[]
    );

    if (!isEmpty(enabledProductFeatures)) {
      void adminService
        .enableFeaturesForCompany(this.props.client as ApolloClient<object>, {
          companyId: company.id,
          featureIds: enabledProductFeatures,
        })
        .then(() => {
          toast.success("Successfully enabled features.");
        })
        .catch((err) => {
          toast.error(wrapError(err));
        });
    }
    if (!this.props.hasPassedKyb) {
      void this.props
        .updateCompanyKybInfo(
          this.props.client as ApolloClient<object>,
          company
        )
        .then((result) => {
          if (result.success) {
            toast.success("Successfully registered company.");
            const path =
              this.state.companyState === EmployerTermsComplete
                ? "owners"
                : "summary";

            this.props.push(
              `/employers/${String(this.props.companyId)}/kyb/${path}`
            );
          }
        })
        .catch((err) => {
          toast.error(wrapError(err));
        });
    } else {
      void this.props
        .updateCompanyInfo(this.props.client as ApolloClient<object>, company)
        .then((result) => {
          if (result.success) {
            toast.success("Successfully updated company.");

            this.props.onUpdate!();
          }
        })
        .catch((err) => {
          toast.error(wrapError(err));
        });
    }
  };

  _closeModal = () => {
    this.setState({ showAddressModal: false });
  };

  _handleProductFeatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      enabledProductFeatures: {
        ...this.state.enabledProductFeatures,
        [e.target.name]: e.target.checked,
      },
    });
  };

  render() {
    const { company } = this.state;
    var articleStyle = {
      paddingBottom: 0,
    };

    if (this.state.loading) {
      return <IconSpinner centered />;
    }

    return (
      <div
        className="tab-pane"
        id="company"
        role="tabpanel"
        aria-labelledby="company_tab"
      >
        <div>
          <Formik
            validateOnChange={false}
            validationSchema={schema}
            onSubmit={(values) => {
              void this.submitCompany(values);
            }}
            enableReinitialize={true}
            initialValues={company}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              setFieldValue,
              errors,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <ScrollToFieldError />
                {this.state.showAddressModal && (
                  <AddressValidator
                    show={this.state.showAddressModal}
                    // @ts-expect-error submitCompany is (FormValues, boolean) => Promise<void>; AddressValidator.onSubmit wants (AddressValidatorAddress, boolean) => void. Behavior-compatible — AddressValidator calls it with the merged address and validate=true.
                    onSubmit={this.submitCompany}
                    setFieldValue={setFieldValue}
                    // @ts-expect-error state.validatedAddress is a loose object (init {} / GraphQL result); AddressValidator narrows to AddressValidatorAddress.
                    validatedAddress={this.state.validatedAddress}
                    onClose={this._closeModal}
                    // @ts-expect-error CompanyFormValues is a closed interface (no index signature); AddressValidatorAddress requires `[key: string]: unknown`. The form values are a superset of the address fields AddressValidator reads — passed through unchanged from JS.
                    values={values}
                    allowPoBox={true}
                  />
                )}
                <div className="mega-container">
                  <div id="form-employer-company-profile">
                    <section>
                      <article className="col-form" style={articleStyle}>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicBusinessName"
                        >
                          <h4 className="page-title">Company Registration</h4>
                          <Form.Control
                            name="businessName"
                            placeholder="Company Legal Name"
                            disabled={this.props.hasPassedKyb}
                            value={values.businessName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touched.businessName && !!errors.businessName
                            }
                            isValid={
                              touched.businessName && !errors.businessName
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.businessName}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicBusinessDBA"
                        >
                          <Form.Control
                            name="businessDBA"
                            placeholder="Company DBA"
                            value={values.businessDBA}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touched.businessDBA && !!errors.businessDBA
                            }
                            isValid={touched.businessDBA && !errors.businessDBA}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.businessDBA}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Row className="mb-3" style={{ marginBottom: 12 }}>
                          <Form.Group
                            as={Col}
                            sm={4}
                            controlId="formBasicBusinessEIN"
                          >
                            <Form.Control
                              as={MaskedInput}
                              mask="00-0000000"
                              name="ein"
                              placeholder="EIN"
                              disabled={this.props.hasPassedKyb}
                              readOnly={this.props.hasPassedKyb}
                              value={values.ein}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={touched.ein && !!errors.ein}
                              isValid={touched.ein && !errors.ein}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.ein}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group
                            as={Col}
                            sm={6}
                            controlId="formBasicBusinessType"
                          >
                            <Form.Select
                              // @ts-expect-error inert: Form.Select has no `sm` grid prop (it's not a Col); preserved from JS.
                              sm={4}
                              name="businessType"
                              value={values.businessType}
                              disabled={this.props.hasPassedKyb}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={
                                touched.businessType && !!errors.businessType
                              }
                              isValid={
                                touched.businessType && !errors.businessType
                              }
                            >
                              <option value="" disabled>
                                Incorporation Type
                              </option>
                              {businessTypes.map((item) => (
                                <option value={item.value} key={item.value}>
                                  {item.description}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {errors.businessType}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            md={4}
                            controlId="formBasicDateOfIncorporation"
                          >
                            <Form.Control
                              as={MaskedInput}
                              mask="00/00/0000"
                              name="registrationDate"
                              placeholder="Date of Incorporation"
                              value={values.registrationDate}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={
                                touched.registrationDate &&
                                !!errors.registrationDate
                              }
                              isValid={
                                touched.registrationDate &&
                                !errors.registrationDate
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.registrationDate}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>

                        <Form.Group
                          className="mb-3"
                          controlId="formBasicBusinessNAICS"
                        >
                          <h4 className="page-title">Company Info</h4>
                          <Form.Select
                            // @ts-expect-error inert: Form.Select has no `sm` grid prop (it's not a Col); preserved from JS.
                            sm={6}
                            disabled={this.props.hasPassedKyb}
                            name="naicsCode"
                            value={values.naicsCode}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.naicsCode && !!errors.naicsCode}
                            isValid={touched.naicsCode && !errors.naicsCode}
                          >
                            <option value="" disabled>
                              Industry Type
                            </option>
                            {naicsValues.map((item) => (
                              <option value={item.value} key={item.value}>
                                {item.description}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.naicsCode}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicBusinessURL"
                        >
                          <Form.Control
                            name="businessURL"
                            placeholder="Website"
                            value={values.businessURL}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touched.businessURL && !!errors.businessURL
                            }
                            isValid={touched.businessURL && !errors.businessURL}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.businessURL}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            sm={6}
                            controlId="formBasicBusinessSize"
                          >
                            <Form.Select
                              name="businessSize"
                              disabled={this.props.hasPassedKyb}
                              value={values.businessSize}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={
                                touched.businessSize && !!errors.businessSize
                              }
                              isValid={
                                touched.businessSize && !errors.businessSize
                              }
                            >
                              <option value="" disabled>
                                Company Size
                              </option>
                              {companySizeValues.map((item) => (
                                <option value={item.value} key={item.value}>
                                  {item.description}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {errors.businessSize}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicAddress1"
                        >
                          <h4 className="page-title">Company Address</h4>
                          <Form.Control
                            name="address1"
                            placeholder="Address 1"
                            value={values.address1}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.address1 && !!errors.address1}
                            isValid={touched.address1 && !errors.address1}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.address1}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicAddress2"
                        >
                          <Form.Control
                            name="address2"
                            placeholder="Address 2"
                            value={values.address2}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.address2 && !!errors.address2}
                            isValid={touched.address2 && !errors.address2}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.address2}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formBasicCity">
                            <Form.Control
                              name="city"
                              placeholder="City"
                              value={values.city}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.city && !!errors.city}
                              isValid={touched.city && !errors.city}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.city}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group
                            as={Col}
                            sm={4}
                            controlId="formBasicState"
                          >
                            <StateSelect
                              value={values.state}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.state}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group
                            as={Col}
                            sm={2}
                            controlId="formBasicPostalCode"
                          >
                            <Form.Control
                              name="postalCode"
                              placeholder="Zip"
                              value={values.postalCode}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={
                                touched.postalCode && !!errors.postalCode
                              }
                              isValid={touched.postalCode && !errors.postalCode}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.postalCode}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            sm={4}
                            controlId="formBasicPhone"
                          >
                            <Form.Control
                              as={MaskedInput}
                              mask="000-000-0000"
                              name="phone"
                              placeholder="Phone"
                              value={values.phone}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={touched.phone && !!errors.phone}
                              isValid={touched.phone && !errors.phone}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.phone}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group
                            as={Col}
                            sm={2}
                            controlId="formBasicPhoneExt"
                          >
                            <Form.Control
                              name="phoneExt"
                              placeholder="Ext"
                              value={values.phoneExt}
                              onChange={handleChange}
                              isInvalid={touched.phoneExt && !!errors.phoneExt}
                              onBlur={handleBlur}
                              isValid={touched.phoneExt && !errors.phoneExt}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.phoneExt}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Form.Group className="mb-3" controlId="formPlanType">
                          <h4 className="page-title">Plan Types</h4>
                          {this.state.planTypes.map((planType, index) => (
                            <>
                              <Form.Check
                                key={index}
                                type="checkbox"
                                // @ts-expect-error planType.id is a number (featureId); Form.Check `id` expects string. Inert at runtime, preserved from JS.
                                id={planType.id}
                                // @ts-expect-error planType.id is a number (featureId); Form.Check `name` expects string. Drives _handleProductFeatureChange keying — preserved from JS.
                                name={planType.id}
                                checked={
                                  this.state.enabledProductFeatures[planType.id]
                                }
                                label={planType.label}
                                // @ts-expect-error planTypeError is a string; Form.Check `isInvalid` expects boolean. Truthy string toggles invalid styling — preserved from JS.
                                isInvalid={this.state.planTypeError}
                                feedback={
                                  index === 2 && this.state.planTypeError
                                }
                                onChange={this._handleProductFeatureChange}
                              />
                            </>
                          ))}
                        </Form.Group>
                        <Form.Group
                          as={Col}
                          sm={6}
                          controlId="formEinValidation"
                        ></Form.Group>
                      </article>
                    </section>
                    <div className="submit-row">
                      {this.props.error && (
                        <Alert type="error" msg={this.props.error} />
                      )}

                      <Button
                        withArrow={true}
                        name="submit"
                        btnLabel="Update Company"
                        loading={
                          this.state.fetchingAddress || this.props.isFetching
                        }
                      />
                      <Button
                        name="cancel"
                        btnLabel={"Back"}
                        type={"button"}
                        color="cancel"
                        onClick={this.props.onBack}
                      />
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(Company));
